import { FC, Fragment, useEffect, useState } from 'react';
import {
  Button,
  Grid,
  Box,
  MainContentBox,
  Paper,
  Typography,
  Link,
  Divider,
  Form,
  NumberField,
} from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { SurchargeHeader } from '../../../components/surchargeHeader';
import NavigationHeader from '../../../components/navigationHeader';
import * as utils from '../../../utility/utils';
import { ReportProps, ReportValues, SurchargeStatusType } from '../../../types';

const InitialReport: FC<ReportProps> = ({ selectedValues, metadata }) => {
  const { t } = useTranslation();

  const [values, setValues] = useState<ReportValues>({
    invoicedKwhPrivateHouseholds:
      selectedValues?.invoicedKwhPrivateHouseholds ?? 0,
    noOfUniqueIdsInNetwork: selectedValues?.noOfUniqueIdsInNetwork ?? 0,
    noOfUniqueIdsInvoiced: selectedValues?.noOfUniqueIdsInvoiced ?? 0,
    amountInvoiced: selectedValues?.amountInvoiced ?? 0,
    sumInvoicedPreviously: selectedValues?.sumInvoicedPreviously ?? 0,
    invoicedKwh: selectedValues?.invoicedKwh ?? 0,
    organizationName: selectedValues?.organizationName,
    organizationNumber: selectedValues?.organizationNumber,
    year: selectedValues?.year,
    sequence: selectedValues?.sequence,
    deadline: selectedValues?.deadline,
    isCorrection: selectedValues?.isCorrection ?? false,
    previouslyReported: 0,
  });

  const {
    invoicedKwhPrivateHouseholds,
    noOfUniqueIdsInNetwork,
    noOfUniqueIdsInvoiced,
    amountInvoiced,
    invoicedKwh,
    sequence,
    year,
  } = values;
  const { paslagForKWh, paslagForMalepunktId } = metadata ?? {};

  useEffect(() => {
    if (paslagForKWh) {
      const invoiceAmount = parseFloat(
        (paslagForKWh * invoicedKwhPrivateHouseholds).toFixed(2)
      );
      setValues((prev) => ({ ...prev, invoicedKwh: invoiceAmount }));
    }
  }, [invoicedKwhPrivateHouseholds]);

  return (
    <Fragment>
      <MainContentBox padding="xtOnly">
        <NavigationHeader selectedTab={1}></NavigationHeader>
        <Paper>
          <Grid container>
            <Form className="d-flex f-column">
              <SurchargeHeader
                sequence={sequence}
                year={year}
                statusType={SurchargeStatusType.Draft}
                version={null}
              ></SurchargeHeader>
              <Box
                padding={3}
                display="flex"
                alignItems="start"
                flexDirection="column"
              >
                <Typography variant="h1" component="p" gutterBottom>
                  {t('surcharge.reporting.header')}
                </Typography>
                <Typography paragraph>
                  {t('surcharge.reporting.helperText')}
                </Typography>
                <Typography paragraph>
                  {t('surcharge.reporting.formHelp')}
                  <Link
                    href="../Brukerveiledning.pdf"
                    targetBlank={true}
                    rel="noopener noreferrer"
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onClick={function noRefCheck() {}}
                  >
                    {t('surcharge.reporting.formHelpLink')}
                  </Link>
                </Typography>
                <Grid container>
                  <Grid item xs={12} paddingBottom={3}>
                    <Typography variant="h4" paddingBottom={3}>
                      {t('surcharge.reporting.subHeader')}
                    </Typography>
                    <Divider variant="fullWidth" component="div" />
                    <Typography variant="h4">
                      {t('surcharge.reporting.households')}
                    </Typography>
                    <Divider variant="fullWidth" component="div" />
                  </Grid>
                </Grid>
                <Divider variant="middle" component="div" />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <NumberField
                      id="invoicedKwhThisTerm"
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      decimalPrecision={0}
                      label={t('surcharge.reporting.invoicedKwhThisTerm')}
                      placeholder={t('surcharge.reporting.enter')}
                      marginBottom
                      onValueChange={({ floatValue }) =>
                        setValues((prev) => ({
                          ...prev,
                          invoicedKwhPrivateHouseholds: floatValue ?? 0,
                        }))
                      }
                      value={invoicedKwhPrivateHouseholds}
                      onFocus={utils.Remove0OnFocusEvent}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <NumberField
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      label={t('surcharge.reporting.rateKrPerKwh')}
                      value={paslagForKWh}
                      prefix="kr"
                      disabled
                      marginBottom
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <NumberField
                      id="sumKwhInvoicedThisTerm"
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      label={t('surcharge.reporting.sumKwhInvoicedThisTerm')}
                      value={invoicedKwh}
                      disabled
                      marginBottom
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider variant="fullWidth" component="div" />
                    <Typography variant="h4">
                      {t('surcharge.reporting.subHeaderOther')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <NumberField
                      id="numberOfMeterIds"
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      decimalPrecision={0}
                      HelperBoxProps={{
                        'aria-label': t('helperBox.ariaLabel'),
                        children: t(
                          'surcharge.reporting.noOfOtherMeterIdsAtEndOfTermHelperBox'
                        ),
                      }}
                      label={t(
                        'surcharge.reporting.noOfOtherMeterIdsAtEndOfTerm'
                      )}
                      placeholder={t('surcharge.reporting.enter')}
                      marginBottom
                      onValueChange={({ floatValue }) =>
                        setValues((prev) => ({
                          ...prev,
                          noOfUniqueIdsInNetwork: floatValue ?? 0,
                        }))
                      }
                      value={noOfUniqueIdsInNetwork}
                      onFocus={utils.Remove0OnFocusEvent}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <NumberField
                      id="uniqueMeterIdsInvoicedThisTerm"
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      HelperBoxProps={{
                        'aria-label': t('helperBox.ariaLabel'),
                        children: t(
                          'surcharge.reporting.uniqueMeterIdsInvoicedThisTermHelperBox'
                        ),
                      }}
                      decimalPrecision={0}
                      label={t(
                        'surcharge.reporting.uniqueMeterIdsInvoicedThisTerm'
                      )}
                      placeholder={t('surcharge.reporting.enter')}
                      marginBottom
                      onValueChange={({ floatValue }) =>
                        setValues((prev) => ({
                          ...prev,
                          noOfUniqueIdsInvoiced: floatValue ?? 0,
                        }))
                      }
                      value={noOfUniqueIdsInvoiced}
                      onFocus={utils.Remove0OnFocusEvent}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <NumberField
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      label={t('surcharge.reporting.ratePerMeter')}
                      value={paslagForMalepunktId}
                      disabled
                      marginBottom
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <NumberField
                      id="sumInvoicedMeterIdsThisTerm"
                      inputProps={{ style: { textAlign: 'left' } }}
                      fullWidth
                      HelperBoxProps={{
                        'aria-label': t('helperBox.ariaLabel'),
                        children: t(
                          'surcharge.reporting.sumInvoicedThisTermHelperBox'
                        ),
                      }}
                      label={t('surcharge.reporting.sumInvoicedThisTerm')}
                      placeholder={t('surcharge.reporting.enter')}
                      marginBottom
                      onValueChange={({ floatValue }) =>
                        setValues((prev) => ({
                          ...prev,
                          amountInvoiced: floatValue ?? 0,
                        }))
                      }
                      value={amountInvoiced}
                      onFocus={utils.Remove0OnFocusEvent}
                    />
                  </Grid>
                  <Grid item xs={12} padding={2}>
                    <Divider variant="fullWidth" component="div" />
                  </Grid>
                  <Grid item xs={12} padding={2}>
                    <Box
                      sx={{
                        backgroundColor: '#EFF2F8',
                        border: '2px solid #012263',
                      }}
                    >
                      <Typography variant="h4" padding={2}>
                        {t('surcharge.reporting.sumSurchargeInvoicedThisTerm')}
                      </Typography>
                      <Typography
                        id="sumSurchargeInvoicedThisTerm"
                        padding={2}
                        paddingTop={0}
                      >
                        {amountInvoiced + invoicedKwh}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} padding={2}>
                    <Divider variant="fullWidth" component="div" />
                  </Grid>
                </Grid>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm="auto">
                    <RouterLink
                      to={{
                        pathname: '/surcharge/overview',
                        state: {
                          ...values,
                        },
                      }}
                    >
                      <Button variant="secondary" leftIcon="close">
                        {t('cancel')}
                      </Button>
                    </RouterLink>
                  </Grid>
                  <Grid item xs={12} sm="auto">
                    <RouterLink
                      to={{
                        pathname: '/surcharge/confirm-report',
                        state: {
                          ...values,
                        },
                      }}
                    >
                      <Button
                        id="saveAndContinueBtn"
                        variant="primary"
                        rightIcon="arrow_right"
                      >
                        {t('surcharge.reporting.saveAndContinue')}
                      </Button>
                    </RouterLink>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          </Grid>
        </Paper>
      </MainContentBox>
    </Fragment>
  );
};

export default InitialReport;
